import React, {useContext, useEffect, useState, useCallback, useMemo} from "react";
import {css} from "@emotion/css";
import SortableTable, {TableData} from "@common/SortableTable";
import {sizes} from "patient-ping-remedy/packages/theme";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useAlertStore} from "@/store/alert_store";
import {AxiosError} from "axios";
import {ToastType} from "patient-ping-remedy/packages/toast";
import Helpers from "@/helpers/helpers";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import Header from "@common/pages/Header";
import CreateFormFieldCategoryModal from "@/components/pages/settings/form_fields/CreateFormFieldCategoryModal";
import ConfirmDeleteModal from "@common/ConfirmDeleteModal";

export default function FormFieldCategoriesTable() {
  const {carecoApi} = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const {addAlert} = useAlertStore();

  const [tableData, setTableData] = useState<TableData[]>([]);
  const [removeFormCategory, setRemoveFormCategory] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const {data: formFieldCategories, isLoading, error, isFetching} = useQuery({
    queryKey: ["formFieldCategories"],
    queryFn: () => carecoApi!.getFormFieldCategories(),
    enabled: !!carecoApi,
  });

  const {mutate: removeFormFieldCategory, isPending: isPendingRemoveFormFieldCategory} = useMutation({
    mutationFn: (categoryName: string) => carecoApi!.deleteFormFieldCategory(categoryName),
    onSuccess: () => {
      addAlert({content: "Form field category deleted successfully.", type: ToastType.SUCCESS});
      void queryClient.invalidateQueries({queryKey: ["formFieldCategories"]});
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to delete form field category. ${Helpers.traceId(error)}`, error);
    },
  });

  const formFieldCategoriesList = useMemo(
    () =>
      tableData
        .filter((data) => typeof data.category.value === "string")
        .map((data) => ({
          category: {value: String(data.category.value)},
        })),
    [tableData]
  );

  const mapFormFieldCategories = useCallback(
    (formFieldCategory: string): TableData => ({
      category: {
        tableHeader: "Category",
        key: "category",
        type: "string",
        value: formFieldCategory,
        displayValue: formFieldCategory,
        columnWidth: "20%",
      },
      button: {
        tableHeader: "Remove",
        key: "remove",
        type: "custom",
        value: formFieldCategory,
        displayValue: (
          <Button
            testId={`remove-form-field-category-${formFieldCategory}`}
            onClick={() => {
              setRemoveFormCategory(formFieldCategory);
              setIsRemoveModalOpen(true);
            }}
            disabled={isPendingRemoveFormFieldCategory}
            styleType={StyleType.TERTIARY}
          >
            Remove
          </Button>
        ),
        columnWidth: "5%",
      },
    }),
    [isPendingRemoveFormFieldCategory]
  );

  useEffect(() => {
    if (formFieldCategories) {
      setTableData(formFieldCategories.map(mapFormFieldCategories));
    }
  }, [formFieldCategories, mapFormFieldCategories]);

  const handleConfirmRemove = useCallback(() => {
    removeFormFieldCategory(removeFormCategory);
    setIsRemoveModalOpen(false);
  }, [removeFormFieldCategory, removeFormCategory]);

  return (
    <>
      <Header subtitle={true}>Form field categories</Header>
      <SortableTable
        id={"form-field-categories-table"}
        data={tableData}
        loading={isLoading || isFetching}
        loadError={error}
        header={["Category", ""]}
        sortableColumns={[]}
        searchColumns={["category"]}
        searchPlaceholder={"Search by category"}
        excludeMixpanel={true}
      >
        <Button
          testId={"add-form-field-category-button"}
          onClick={() => setIsModalOpen(true)}
          disabled={isLoading}
        >
          <Icon iconClass={ICONS.plus} className={css({ marginRight: sizes.xSmall })} />
          Add
        </Button>
      </SortableTable>
      {isModalOpen && (
        <CreateFormFieldCategoryModal
          close={() => setIsModalOpen(false)}
          tableData={formFieldCategoriesList}
        />
      )}
      {isRemoveModalOpen && (
        <ConfirmDeleteModal
          close={() => setIsRemoveModalOpen(false)}
          setConfirmRemoveModalOpen={handleConfirmRemove}
          name={removeFormCategory}
          tableName={"form field categories"}
        />
      )}
    </>
  );
}
